import type { SortingState, Updater } from "@tanstack/react-table"
import { useCallback } from "react"
import type { RefObject } from "react"
import type { Client } from "urql"
import { useShallow } from "zustand/react/shallow"

import type { ProductInventoryLotsSearchParams } from "../schemas"
import { useProductInventoryLotsSearchStore } from "../stores/useProductInventoryLotsSearchStore"

import type { InventoryByLotItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

const ROUTE = "/app/inventory/products/$productId/_layout/lots"

interface UseProductInventoryLotListParams {
  productId: number
}

interface UseProductInventoryLotListReturn {
  inventories: InventoryByLotItem[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: ProductInventoryLotsSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleScroll: () => void
}

export const useProductInventoryLotList = ({
  productId,
}: UseProductInventoryLotListParams): UseProductInventoryLotListReturn => {
  const store = useProductInventoryLotsSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const executeSearch = useCallback(
    (client: Client, params: ProductInventoryLotsSearchParams) => {
      store.executeSearch(client, { ...params, productId })
    },
    [store, productId]
  )

  const loadMore = useCallback(
    (client: Client, params: ProductInventoryLotsSearchParams) => {
      store.loadMore(client, { ...params, productId })
    },
    [store, productId]
  )

  const {
    data: inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
  } = useListPageBase<InventoryByLotItem, ProductInventoryLotsSearchParams>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
  })

  return {
    inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
  }
}
